import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, Plus, Check, Zap, Sun, Ruler, Star } from "lucide-react";
import { AspectRatio } from "./ui/aspect-ratio";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { useLang } from "../i18n/LanguageContext";
import { formatEUR, badgeTone } from "../lib/api";

const toneClass = {
  cyan: "text-cyanx bg-cyanx/10", mint: "text-mintx bg-mintx/10",
  amber: "text-amberx bg-amberx/10", slate: "text-foreground/80 bg-white/5",
};

export const BadgePill = ({ label, className = "" }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] sm:px-2.5 sm:text-[11px] font-medium ${toneClass[badgeTone(label)]} hairline ${className}`}>{label}</span>
);

const availTone = { in_stock: "text-mintx", low_stock: "text-amberx", preorder: "text-cyanx", out_of_stock: "text-destructive" };

export const ProductCard = ({ product, index = 0 }) => {
  const { t } = useLang();
  const { addToCart, compare, toggleCompare } = useStore();
  const [qv, setQv] = useState(false);
  const s = product.specs || {};
  const inCompare = compare.includes(product.slug);
  const availKey = { in_stock: "common.inStock", low_stock: "common.lowStock", preorder: "common.preorder", out_of_stock: "common.outStock" }[product.availability] || "common.inStock";

  const add = () => {
    addToCart({ slug: product.slug, name: product.name, price_eur: product.price_eur, image: product.image, kind: "product" });
    toast.success(`${product.name} \u2014 ${t("common.added")}`);
  };

  return (
    <div data-testid="product-card" data-slug={product.slug}
      className="group relative rounded-2xl card-ceramic overflow-hidden flex flex-col animate-fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}>
      <div className="relative">
        <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 flex flex-wrap gap-1.5 max-w-[70%]">
          {(product.badges || []).slice(0, 2).map((b, i) => (
            <BadgePill key={b} label={b} className={i > 0 ? "hidden sm:inline-flex" : ""} />
          ))}
        </div>
        <button aria-label="compare" data-testid="product-compare-checkbox" onClick={() => toggleCompare(product.slug)}
          className={`absolute top-2 right-2 sm:top-3 sm:right-3 z-10 h-7 w-7 sm:h-8 sm:w-8 rounded-full inline-flex items-center justify-center transition-all ${inCompare ? "bg-cyanx text-[#04121a]" : "glass text-foreground/70 hover:text-foreground"}`}>
          {inCompare ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </button>
        <Link to={`/product/${product.slug}`} className="block relative overflow-hidden sweep">
          <div className="absolute inset-0 opacity-60" style={{ background: "radial-gradient(circle at 50% 45%, rgba(86,227,255,0.10), transparent 60%)" }} />
          <AspectRatio ratio={4 / 3}>
            <img src={product.image} alt={product.name} loading="lazy"
              className="h-full w-full object-contain p-2.5 pt-8 sm:p-4 sm:pt-10 transition-transform duration-500 group-hover:scale-105" />
          </AspectRatio>
        </Link>
      </div>

      <div className="p-3 sm:p-4 flex flex-col gap-1.5 sm:gap-2 flex-1">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground">{product.brand}</span>
          <span className="inline-flex items-center gap-1 text-xs text-amberx"><Star className="h-3 w-3 fill-amberx" />{product.rating}</span>
        </div>
        <Link to={`/product/${product.slug}`}><h3 className="font-display text-base sm:text-lg leading-tight hover:text-cyanx transition-colors">{product.name}</h3></Link>
        <p className="hidden sm:block text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{product.highlight}</p>
        <div className="flex flex-wrap gap-1 sm:gap-1.5 mt-0.5 sm:mt-1">
          <span className="chip px-2 py-0.5 text-[10px] sm:px-3 sm:py-1 sm:text-xs"><Sun className="h-3 w-3 text-cyanx" />{s.lumens_ansi} ANSI</span>
          <span className="chip px-2 py-0.5 text-[10px] sm:px-3 sm:py-1 sm:text-xs">{s.resolution}</span>
          <span className="chip hidden sm:inline-flex"><Ruler className="h-3 w-3" />{s.throw_ratio}:1</span>
        </div>
        <div className="mt-auto pt-2 sm:pt-3 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5 sm:gap-2 flex-wrap">
              <span className="font-display text-lg sm:text-xl">{formatEUR(product.price_eur)}</span>
              {product.compare_at_eur && <span className="text-xs text-muted-foreground line-through">{formatEUR(product.compare_at_eur)}</span>}
            </div>
            <span className={`text-[10px] sm:text-[11px] ${availTone[product.availability] || "text-mintx"}`}>● {t(availKey)}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <button data-testid="product-quick-view-button" onClick={() => setQv(true)}
              className="hidden sm:inline-flex h-9 w-9 rounded-lg hairline items-center justify-center hover:shadow-glow-cyan transition-shadow" aria-label={t("common.quickView")}>
              <Eye className="h-4 w-4" />
            </button>
            <Button data-testid="product-add-to-cart-button" onClick={add} size="sm" className="btn-glow border-0 h-8 w-8 p-0 sm:h-9 sm:w-9">
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={qv} onOpenChange={setQv}>
        <DialogContent className="glass border-border max-w-3xl">
          <DialogTitle className="sr-only">{product.name}</DialogTitle>
          <DialogDescription className="sr-only">{product.highlight}</DialogDescription>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="relative rounded-xl overflow-hidden bg-black/40">
              <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 50% 45%, rgba(86,227,255,0.12), transparent 60%)" }} />
              <AspectRatio ratio={1}><img src={product.image} alt={product.name} className="h-full w-full object-contain p-6" /></AspectRatio>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-wrap gap-1.5 mb-2">{(product.badges || []).map((b) => <BadgePill key={b} label={b} />)}</div>
              <h3 className="font-display text-2xl">{product.name}</h3>
              <p className="text-sm text-muted-foreground mt-2">{product.highlight}</p>
              <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                <div className="chip justify-center">{s.lumens_ansi} ANSI</div>
                <div className="chip justify-center">{s.resolution}</div>
                <div className="chip justify-center">{s.throw_type?.replace("_"," ")}</div>
                <div className="chip justify-center">{s.smart_os}</div>
              </div>
              <div className="mt-auto pt-5 flex items-center justify-between">
                <span className="font-display text-2xl">{formatEUR(product.price_eur)}</span>
                <div className="flex gap-2">
                  <Button asChild variant="secondary"><Link to={`/product/${product.slug}`}>{t("common.viewDetails")}</Link></Button>
                  <Button onClick={add} className="btn-glow border-0">{t("common.addToCart")}</Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
